<template>
  <div class="kpi-card" :class="{ 'kpi-card--clickable': clickable }">
    <div class="kpi-card__header">
      <span class="kpi-card__icon" :style="{ backgroundColor: iconBgColor }">
        <font-awesome-icon :icon="icon" />
      </span>
      <span v-if="trend !== null" class="kpi-card__trend" :class="trendClass">
        <font-awesome-icon :icon="trendIcon" />
        {{ Math.abs(trend) }}%
      </span>
    </div>
    <div class="kpi-card__value">
      <span v-if="loading" class="kpi-card__spinner">
        <font-awesome-icon icon="spinner" spin />
      </span>
      <span v-else>{{ formattedValue }}</span>
    </div>
    <div class="kpi-card__label">{{ label }}</div>
    <div v-if="subtitle" class="kpi-card__subtitle">{{ subtitle }}</div>
  </div>
</template>

<script>
export default {
  name: 'KpiCard',
  props: {
    value: {
      type: [Number, String],
      required: true
    },
    label: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: 'chart-line'
    },
    iconBgColor: {
      type: String,
      default: '#B30E10'
    },
    trend: {
      type: Number,
      default: null
    },
    format: {
      type: String,
      default: 'number' // 'number', 'currency', 'time'
    },
    clickable: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    formattedValue() {
      if (this.format === 'currency') {
        return new Intl.NumberFormat('nb-NO', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(this.value) + ' kr';
      }
      if (this.format === 'time') {
        return this.value;
      }
      return new Intl.NumberFormat('nb-NO').format(this.value);
    },
    trendClass() {
      if (this.trend > 0) return 'kpi-card__trend--positive';
      if (this.trend < 0) return 'kpi-card__trend--negative';
      return 'kpi-card__trend--neutral';
    },
    trendIcon() {
      if (this.trend > 0) return 'arrow-up';
      if (this.trend < 0) return 'arrow-down';
      return 'minus';
    }
  }
};
</script>

<style scoped>
.kpi-card {
  background: var(--bo-surface);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card--clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  cursor: pointer;
}

.kpi-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.kpi-card__icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bo-surface);
  font-size: 16px;
}

.kpi-card__trend {
  font-size: 13px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.kpi-card__trend--positive {
  background-color: var(--bo-ok-bg);
  color: var(--bo-success-ink);
}

.kpi-card__trend--negative {
  background-color: var(--bo-bad-bg);
  color: var(--bo-alert-ink);
}

.kpi-card__trend--neutral {
  background-color: var(--bo-surface-2);
  color: var(--bo-muted);
}

.kpi-card__value {
  font-size: 28px;
  font-weight: 700;
  color: var(--bo-ink);
  margin-bottom: 4px;
}

.kpi-card__label {
  font-size: 14px;
  color: var(--bo-muted);
  font-weight: 500;
}

.kpi-card__subtitle {
  font-size: 12px;
  color: var(--bo-muted-2);
  margin-top: 4px;
}

.kpi-card__spinner {
  color: var(--bo-ink);
}
</style>
